import type { ReadingReward } from '../core/game';

export interface RewardSequence {
  boardId: string;
  readings: ReadingReward[];
  hintCount: number;
  completed: boolean;
  exceeded: boolean;
  onHint: (count: number) => void;
}
export interface RewardEffects {
  currentBoard: () => string | undefined;
  reducedMotion: () => boolean;
  cell: (index: number) => HTMLElement | null;
  hints: HTMLElement;
  score: HTMLElement;
  card: HTMLElement;
  chime: (kind: 'success' | 'special' | 'complete' | 'exceed') => void;
}

/** Presentation only: points are committed and saved by the game before enqueue. */
export class RewardFeedback {
  private epoch = 0;
  private tail: Promise<void> = Promise.resolve();
  private animations = new Set<Animation>();
  private decorations = new Set<HTMLElement>();
  private queued = 0;
  get running(): boolean { return this.queued > 0; }
  constructor(private effects: RewardEffects) {}

  cancel(): void {
    this.epoch++; this.queued = 0; this.tail = Promise.resolve();
    delete this.effects.card.dataset.rewardDirection; delete this.effects.card.dataset.rewardWord;
    for (const animation of this.animations) animation.cancel();
    this.animations.clear();
    for (const node of this.decorations) node.remove();
    this.decorations.clear();
  }
  private async animate(node: HTMLElement, frames: Keyframe[], options: KeyframeAnimationOptions): Promise<void> {
    const animation = node.animate(frames, options);
    this.animations.add(animation);
    try { await animation.finished; } catch { /* New board/background cancellation. */ }
    finally { this.animations.delete(animation); }
  }
  private decoration(className: string, parent: HTMLElement): HTMLElement {
    const node = document.createElement('i'); node.className = className; node.setAttribute('aria-hidden', 'true');
    parent.append(node); this.decorations.add(node); return node;
  }
  private remove(node: HTMLElement): void { node.remove(); this.decorations.delete(node); }

  enqueue(sequence: RewardSequence): Promise<void> {
    const epoch = this.epoch;
    const live = () => epoch === this.epoch && sequence.boardId === this.effects.currentBoard() && !document.hidden;
    this.queued++;
    const task = this.tail.catch(() => {}).then(async () => {
      if (!live()) return;
      for (const reading of sequence.readings) {
        if (!live()) return;
        const reduced = this.effects.reducedMotion();
        const nodes = reading.cells.map(index => this.effects.cell(index)).filter((cell): cell is HTMLElement => !!cell)
          .map(cell => this.decoration('tile-reward', cell));
        this.effects.card.dataset.rewardDirection = reading.direction;
        this.effects.card.dataset.rewardWord = reading.word;
        this.effects.chime('success');
        try {
          await Promise.all(nodes.map((node, index) => this.animate(node, [
            { opacity: 0 }, { opacity: 1, offset: 0.18 }, { opacity: 1, offset: 0.65 }, { opacity: 0 },
          ], { duration: reduced ? 160 : 900, delay: reduced ? 0 : index * 100, easing: 'ease-in-out', fill: 'backwards' })));
        } finally { nodes.forEach(node => this.remove(node)); }
      }
      for (let index = 0; index < sequence.hintCount; index++) {
        if (!live()) return;
        const reduced = this.effects.reducedMotion();
        const source = this.effects.hints.getBoundingClientRect(), destination = this.effects.score.getBoundingClientRect();
        const token = this.decoration('hint-reward-token', document.body); token.textContent = '💡';
        token.style.left = `${source.left + source.width / 2}px`; token.style.top = `${source.top + source.height / 2}px`;
        try {
          await this.animate(token, reduced ? [{ opacity: 0 }, { opacity: 1 }, { opacity: 0 }] : [
            { transform: 'translate(-50%, -50%) scale(.8)', opacity: 0 },
            { transform: 'translate(-50%, -80%) scale(1.2)', opacity: 1, offset: 0.2 },
            { transform: `translate(calc(-50% + ${destination.left + destination.width / 2 - source.left - source.width / 2}px), calc(-50% + ${destination.top + destination.height / 2 - source.top - source.height / 2}px)) scale(.25)`, opacity: 0 },
          ], { duration: reduced ? 160 : 600, easing: 'cubic-bezier(.2,.7,.4,1)' });
        } finally { this.remove(token); }
        if (!live()) return;
        sequence.onHint(index + 1); this.effects.chime('special');
        await this.animate(this.effects.score, reduced ? [{ color: '#ffe3a0' }, { color: 'inherit' }] : [
          { transform: 'scale(1)' }, { transform: 'scale(1.12)', color: '#ffe3a0', offset: .4 }, { transform: 'scale(1)' },
        ], { duration: reduced ? 120 : 220 });
      }
      if (!live()) return;
      if (sequence.exceeded || sequence.completed) {
        this.effects.chime(sequence.exceeded ? 'exceed' : 'complete');
        await this.animate(this.effects.card, [
          { boxShadow: '0 0 0 0 #f4c45f00' }, { boxShadow: '0 0 30px 5px #f4c45f88', offset: .4 }, { boxShadow: '0 0 12px 1px #f4c45f22' },
        ], { duration: this.effects.reducedMotion() ? 160 : 650 });
      }
    }).finally(() => {
      if (epoch === this.epoch) {
        this.queued--;
        if (!this.queued) { delete this.effects.card.dataset.rewardDirection; delete this.effects.card.dataset.rewardWord; }
      }
    });
    this.tail = task;
    return task;
  }
}
